import React, { useState, useEffect } from 'react';
import { makeStyles } from '@material-ui/core/styles';
import { DateTime } from 'luxon';
import { DatePicker } from "@material-ui/pickers";

import Dialog from '@material-ui/core/Dialog';
import DialogActions from '@material-ui/core/DialogActions';
import DialogContent from '@material-ui/core/DialogContent';
import DialogTitle from '@material-ui/core/DialogTitle';

import Button from '@material-ui/core/Button';
import IconButton from '@material-ui/core/IconButton';
import Typography from '@material-ui/core/Typography';
import TextField from '@material-ui/core/TextField';

import CloseIcon from '@material-ui/icons/Close';

import Loading from 'app/components/loaders/Loading';

import useApi from 'app/context/useApi';

const useStyles = makeStyles(theme => ({
    closeButton: {
        position: 'absolute',
        right: theme.spacing(1),
        top: theme.spacing(1),
        color: theme.palette.grey[500],
    },
}));

export default function BatchPaymentDialog(props) {

    const { ids, open, onClose, onClosed, onSave, type } = props;

    const [ description, setDescription ] = useState("");
    const [ paidAt, setPaidAt ] = useState(DateTime.local());

    const [ loading, setLoading ] = useState(false);
    const [ errors, setErrors ] = useState({});

    const classes = useStyles();
    const api = useApi();

    // Setter Functions -----------------------------------------------

    const handleDescription = e => {

        const value = e.target.value;

        if (isError('description')) delete errors['description'];

        setDescription(value);
    };

    const handlePaidAt = value => {
        if (isError('paid_at')) delete errors['paid_at'];

        setPaidAt(value)
    }

    // Post Functions -----------------------------------------------

    const postBatchPayment = () => {
        setLoading(true);

        const data = {
            paid_at: paidAt.toString(),
            description: description,
            invoice_ids: ids
        }

        api.post(`/admin/${type}/payment/batch`, data)
            .then(() => onSave())
            .catch(error => {
                setLoading(false);

                if (error?.response?.data?.errors) {
                    setErrors(error.response.data.errors)
                }
            }).finally(() => setLoading(false));
    }

    const isError = field => {
        if (errors[field]) return true;
        return false;
    }

    const getErrorMessage = field => {
        if (isError(field)) return errors[field];
        return "";
    }

    const onExited = () => {
        setLoading(false);
        if (onClosed) onClosed();
    }

    return (
        <Dialog
            aria-labelledby="batch-payment-dialog-title"
            open={open}
            aria-describedby="batch-payment-dialogue-description"
            fullWidth
            maxWidth="xs"
            onExited={onExited}
            onClose={onClose}
            >
            <Loading isLoading={loading}/>
            <DialogTitle id="batch-payment-dialog-title">
                <span>Batch Payment</span>
                <IconButton aria-label="Cancel" className={classes.closeButton} onClick={onClose}>
                    <CloseIcon />
                </IconButton>
            </DialogTitle>
            <DialogContent dividers>
                <Typography variant="subtitle1" color="textSecondary" gutterBottom>
                    Set the description and date paid for all selected invoices. It will set the payment method to manual and set the amount to the total for each invoice.
                </Typography>
                <TextField
                    id="payment-description"
                    label="Description"
                    placeholder="(i.e. check #)"
                    multiline
                    rows="4"
                    rowsMax="4"
                    variant="outlined"
                    fullWidth
                    value={description}
                    error={isError('description')}
                    onChange={handleDescription}
                    helperText={getErrorMessage('description')}
                    margin="normal"
                />
                <DatePicker
                    label="Date Paid"
                    inputVariant="outlined"
                    value={paidAt}
                    onChange={handlePaidAt}
                    minutesStep={5}
                    fullWidth
                    margin="normal"
                    format="MM/dd/yyyy"
                    error={isError('paid_at')}
                    helperText={getErrorMessage('paid_at')}
                />
            </DialogContent>
            <DialogActions>
                <Button
                    onClick={postBatchPayment}
                    color="primary"
                    variant="contained"
                    disabled={loading}
                    >
                    Save Payments
                </Button>
            </DialogActions>
        </Dialog>
    );
}
